import * as graphics from "@client/app/graphics";
import {TouchHandler} from "@client/components/TouchHandler";
import {useWorkoutData} from "@client/features/workouts";
import * as forms from "@client/forms";
import {createRouter, routerFactory} from "@client/gotterdammerung";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import {useSiteContext} from "@client/site/context";
import {useAppDispatch} from "@client/store";
import {classNames, server} from "@reactivated";
import React from "react";

const exerciseCategories = {
    WEIGHT: {
        title: "Weight Training",
        className: "bg-pink-50 text-pink-800 ring-pink-600/20",
    },
    BODYWEIGHT: {
        title: "Bodyweight",
        className: "bg-pink-50 text-pink-800 ring-pink-600/20", // Using similar styling as WEIGHT
    },
    CARDIO: {title: "Cardio", className: "bg-sky-50 text-sky-700 ring-sky-600/20"},
    REPS_DISTANCE: {
        title: "Duration & Distance",
        className: "bg-sky-50 text-sky-700 ring-sky-600/20",
    },
    DURATION_DISTANCE: {
        title: "Duration & Distance",
        className: "bg-sky-50 text-sky-700 ring-sky-600/20",
    },
    MEDLEY: {
        title: "Medley",
        className: "bg-violet-50 text-violet-700 ring-violet-600/20",
    },
    DURATION: {
        title: "Duration",
        className: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
    },
    WEIGHT_DISTANCE: {
        title: "Weight & Distance",
        className: "bg-sky-50 text-sky-700 ring-sky-600/20",
    },
    WEIGHT_DURATION_DISTANCE: {
        title: "Weight, Duration & Distance",
        className: "bg-rose-50 text-rose-700 ring-rose-600/20",
    },
};

function getFirstLetter(exerciseType: server.journal.workouts.ExerciseType.output) {
    if (exerciseType.name.startsWith("Barbell ")) {
        return exerciseType.name.replace("Barbell ", "")[0].toUpperCase();
    }
    return exerciseType.name[0].toUpperCase();
}

export function ListExerciseType(props: {
    showCategories: boolean;
    exerciseType: server.journal.workouts.ExerciseType.output;
    profile: server.core.rpc.Profile.output | null;
    behavior: {onClick: () => unknown};
}) {
    const {exerciseType} = props;

    return (
        <TouchHandler
            key={exerciseType.uuid}
            className="flex items-center gap-2 px-0 py-1.5"
            onClick={"onClick" in props.behavior ? props.behavior.onClick : undefined}
        >
            {exerciseType.media?.image_uid != null ? (
                <div>
                    <img
                        className="size-12 object-scale-down sm:size-20"
                        src={`https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg/${exerciseType.media.image_uid}/thumbnail`}
                    />
                </div>
            ) : (
                <div className="flex size-12 items-center justify-center rounded-xs bg-indigo-100 sm:size-20">
                    <span className="font-semibold">
                        {getFirstLetter(exerciseType)}
                    </span>
                </div>
            )}
            <div className="flex-1 space-y-1">
                <h2 className="text-sm sm:text-[15px]">{exerciseType.name}</h2>

                <div className="flex flex-wrap gap-1">
                    {props.showCategories && (
                        <span
                            className={classNames(
                                "inline-flex shrink-0 items-center rounded-full px-3 py-0.5 text-xs font-mediumring-1 ring-1 ring-inset font-medium",
                                exerciseCategories[exerciseType.category].className,
                            )}
                        >
                            {exerciseCategories[exerciseType.category].title}
                        </span>
                    )}

                    {(exerciseType.video_uid != null ||
                        exerciseType.video_url != null) && (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-green-50 px-3 py-0.5 text-xs font-medium text-green-700 ring-1 ring-sky-600/20 ring-inset">
                            Video
                        </span>
                    )}
                    {props.profile != null &&
                        exerciseType.user_id == props.profile.user.id && (
                            <span className="inline-flex shrink-0 items-center rounded-full bg-sky-50 px-3 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-sky-600/20 ring-inset">
                                Custom
                            </span>
                        )}
                </div>
            </div>
            <div
                className={classNames(
                    "flex flex-col items-center justify-center h-6 opacity-80 -mr-1.5 ml-1",
                )}
            >
                <graphics.ElGrecoCustomChevronRight />
            </div>
        </TouchHandler>
    );
}

function getMostLikelyExercises(
    library: server.journal.workouts.ExerciseType.output[],
    selected: UUID[],
    exerciseTypeUsers: server.journal.workouts.ExerciseTypeUser.output[], // Renamed for clarity
    limit: number,
) {
    if (selected.length === 0) {
        // Suggest based on how often an exercise is the first in a workout
        const sortedByFirstName = [...exerciseTypeUsers]
            .filter((etu) => etu.first_in_workout_count > 0) // Consider only those that have been first
            .sort((a, b) => b.first_in_workout_count - a.first_in_workout_count);

        const mostLikelyFirst = sortedByFirstName
            .map((etu) =>
                library.find((libEx) => libEx.uuid === etu.exercise_type.uuid),
            )
            .filter((ex) => ex != null);

        return mostLikelyFirst.slice(0, limit);
    }

    // Existing logic for co-occurring exercises when some are already selected
    const selectedSet = new Set(selected);

    const allRelated = exerciseTypeUsers // Use the passed 'exerciseTypeUsers'
        .filter((entry) => selectedSet.has(entry.exercise_type.uuid))
        .flatMap((entry) => entry.related);

    const freqMap = allRelated.reduce<Record<string, number>>((acc, uuid) => {
        acc[uuid] = (acc[uuid] || 0) + 1;
        return acc;
    }, {});

    const sortedByFreq = Object.keys(freqMap).sort((a, b) => freqMap[b] - freqMap[a]);
    const unselected = sortedByFreq.filter((uuid) => !selectedSet.has(uuid as UUID));
    const topUuids = unselected.slice(0, limit);

    const mostLikely = topUuids
        .map((uuid) => library.find((ex) => ex.uuid === uuid))
        .filter(
            (ex): ex is server.journal.workouts.ExerciseType.output => ex !== undefined,
        );

    return mostLikely;
}

export type Input = {
    disableCreate?: boolean;
    exerciseTypesForPrediction?: UUID[];
    filter?: (exerciseType: server.journal.workouts.ExerciseType.output) => boolean;
};

export type Output = Array<server.journal.workouts.ExerciseType.output> | null;

export const InputContext = React.createContext<
    Input & {close: (result: Output) => void}
>(null!);

const useHooks = () => {
    const props = React.useContext(InputContext);
    const dispatch = useAppDispatch();
    const workouts = useWorkoutData();

    const [filters, setFilters] = React.useState<Record<string, boolean | null>>({});

    const {profile} = useSiteContext();

    const _allExerciseTypes =
        workouts?.exercise_types.filter(props.filter ?? (() => true)) ?? [];

    const mostLikely = getMostLikelyExercises(
        _allExerciseTypes,
        props.exerciseTypesForPrediction ?? [],
        workouts?.exercise_type_users ?? [], // Pass the exerciseTypeUsers data
        5,
    );
    const mostLikelySet = new Set(mostLikely.map((e) => e.uuid));

    const allExerciseTypes = [
        ...mostLikely,
        ..._allExerciseTypes.filter((e) => !mostLikelySet.has(e.uuid)),
    ];

    const noFiltersSelected = Object.values(filters).every((v) => v == false);

    const exerciseTypes = allExerciseTypes.filter((exerciseType) => {
        return (
            (filters.Barbell == true &&
                exerciseType.name.toLowerCase().includes("barbell")) ||
            (filters.Dumbbell == true &&
                exerciseType.name.toLowerCase().includes("dumbbell")) ||
            (filters.Kettlebell == true &&
                exerciseType.name.toLowerCase().includes("kettlebell")) ||
            (filters.Cardio == true && exerciseType.category == "CARDIO") ||
            (filters.Other == true &&
                !["barbell", "dumbbell", "kettlebell"].some((keyword) =>
                    exerciseType.name.toLowerCase().includes(keyword),
                ) &&
                exerciseType.category != "CARDIO") ||
            Object.values(filters).every((v) => v == false)
        );
    });

    const showCategories = noFiltersSelected || filters.Other == true;

    const exerciseTypeForm = forms.useForm(server.journal.workouts.ExerciseTypeForm);
    const query = exerciseTypeForm.values.name.trim();

    const filtered =
        query === ""
            ? exerciseTypes
            : exerciseTypes.filter((exerciseType) => {
                  return exerciseType.name.toLowerCase().includes(query.toLowerCase());
              });

    const exactMatch =
        query === "" ||
        allExerciseTypes.filter(
            (person) => person.name.toLowerCase() == query.toLowerCase(),
        ).length == 1;

    const createExerciseType = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        const response = await forms.submitForm(exerciseTypeForm, (data) =>
            server.journal.workouts.create_exercise_type({
                ...data,
                uuid: crypto.randomUUID(),
            }),
        );

        if (response?.type == "success") {
            dispatch({type: "CREATED_EXERCISE_TYPE", exerciseType: response.data});
            props.close([response.data]);
            void analytics.track("CREATED_EXERCISE_TYPE", {
                exercise_name: response.data.name,
            });
        }
    };

    return {
        createExerciseType,
        noFiltersSelected,
        showCategories,
        filters,
        setFilters,
        workouts,
        exerciseTypeForm,
        filtered,
        profile,
        query,
        exactMatch,
        disableCreate: props.disableCreate,
    };
};

export const HooksContext = React.createContext<ReturnType<typeof useHooks>>(null!);

export function Hooks(props: {children: React.ReactNode}) {
    const hooks = useHooks();

    return (
        <HooksContext.Provider value={hooks}>{props.children}</HooksContext.Provider>
    );
}

export const List = routerFactory()(() => {
    const input = React.useContext(InputContext);
    const hooks = React.useContext(HooksContext);

    return {input, close: input.close, ...hooks};
})({
    name: "list",
    path: "",
});

export const Create = List.subroute({
    name: "create",
    path: "create/",
});

export const router = createRouter([List, Create], "memory");

export type Router = typeof router;

export const ListRoute = List.withHooks(() => {})
    .withIndex((props) => (
        <div className="flex flex-col divide-y divide-gray-200">
            {props.workouts == null && <div>Loading...</div>}
            {props.filtered.map((exerciseType) => (
                <ListExerciseType
                    key={exerciseType.uuid}
                    showCategories={true}
                    exerciseType={exerciseType}
                    profile={props.profile ?? null}
                    behavior={{
                        onClick: () => {
                            props.close([exerciseType]);
                        },
                    }}
                />
            ))}
            {props.query != "" && props.filtered.length == 0 && (
                <p>No existing exercises found.</p>
            )}
        </div>
    ))
    .withActions(() => [])
    .withOptions((props) => ({
        back: "Back",
        title: "Exercise Library",
        topSection: (
            <div>
                <forms.Field
                    noLabel
                    field={props.exerciseTypeForm.fields.name}
                    placeholder="Exercise name"
                />
                <div className="mt-2 flex justify-between gap-1">
                    {["Barbell", "Dumbbell", "Kettlebell", "Cardio", "Other"].map(
                        (item) => (
                            <button
                                onClick={() => {
                                    props.setFilters((prev) => ({
                                        ...prev,
                                        [item]: !(prev[item] ?? false),
                                    }));
                                }}
                                key={item}
                                className={classNames(
                                    "inline-flex justify-center items-center rounded-md sm:flex-1 sm:py-2 sm:text-sm px-2 py-1 text-xs font-medium ring-1 ring-inset",
                                    props.filters[item] == true
                                        ? "bg-indigo-50 text-indigo-700 ring-indigo-700/10"
                                        : "bg-gray-50 text-gray-600 ring-gray-500/10",
                                )}
                            >
                                {item}
                            </button>
                        ),
                    )}
                </div>
            </div>
        ),
        bottomSection: props.exactMatch == false && props.disableCreate != true && (
            <typography.ImportantAction
                onClick={() => {
                    void router.push("create.index", {});
                }}
                title={`Create exercise “${props.query}”`}
            />
        ),
    }));

export const CreateRoute = Create.withHooks(() => {})
    .withIndex((props) => (
        <form
            id="create-exercise-type-form"
            className="flex flex-col gap-4"
            onSubmit={props.createExerciseType}
        >
            <forms.Field field={props.exerciseTypeForm.fields.name} />
            <forms.Field field={props.exerciseTypeForm.fields.category} />
            {props.profile?.user.is_manager == true && (
                <forms.Field field={props.exerciseTypeForm.fields.video_url} />
            )}
            <forms.Field field={props.exerciseTypeForm.fields.description} />
        </form>
    ))
    .withActions((props) => [
        {
            icon: "save",
            title: "Create exercise",
            onClick: () => {
                void props.createExerciseType();
            },
        },
    ])
    .withOptions(() => ({
        back: "Exercises",
        title: "Create exercise",
    }));

export const {Route} = router.mount({
    list: ListRoute,
    create: CreateRoute,
});
